import React, { Component } from "react";
import { withApollo } from "react-apollo";
import gql from "graphql-tag";
import "../../theme/_fdn-theme.scss";
import "./_foundationTest.scss";

/**
 * Test runner.
 * - Loads questions via startFoundationTest.
 * - Time is derived from the number of questions: perQuestionSeconds x questions
 *   (default 60s per question), so a longer paper gets proportionally more time.
 * - Records each answer AND the seconds spent on that question (saveFoundationTestAnswer).
 * - Auto-submits when the timer hits zero.
 *
 * Renders full-screen as its own page (a left question panel + a right question
 * palette), matching a real exam screen.
 */

const PER_QUESTION_SECONDS = 60;

const START = gql`
  mutation ($chapter_id: Int!, $class_id: Int, $subject_id: Int, $registration_id: Int, $limit: Int) {
    startFoundationTest(chapter_id: $chapter_id, class_id: $class_id, subject_id: $subject_id, registration_id: $registration_id, limit: $limit) {
      attempt_id
      chapter_name
      total_questions
      max_time_seconds
      questions { question_id order question options { id text } }
    }
  }
`;

const START_ERROR = gql`
  mutation ($registration_id: Int!, $class_id: Int, $subject_ids: [Int], $chapter_ids: [Int], $limit: Int) {
    startFoundationErrorTest(registration_id: $registration_id, class_id: $class_id, subject_ids: $subject_ids, chapter_ids: $chapter_ids, limit: $limit) {
      attempt_id
      chapter_name
      total_questions
      max_time_seconds
      questions { question_id order question options { id text } }
    }
  }
`;

const START_CUSTOM = gql`
  mutation ($registration_id: Int!, $class_id: Int, $subject_ids: [Int], $topics: [String], $limit: Int) {
    startFoundationCustomTest(registration_id: $registration_id, class_id: $class_id, subject_ids: $subject_ids, topics: $topics, limit: $limit) {
      attempt_id
      chapter_name
      total_questions
      max_time_seconds
      questions { question_id order question options { id text } }
    }
  }
`;

const SAVE = gql`
  mutation ($attempt_id: Int!, $question_id: String!, $selected_option: String, $time_taken_seconds: Int) {
    saveFoundationTestAnswer(attempt_id: $attempt_id, question_id: $question_id, selected_option: $selected_option, time_taken_seconds: $time_taken_seconds)
  }
`;

const SUBMIT = gql`
  mutation ($attempt_id: Int!, $duration_seconds: Int) {
    submitFoundationTest(attempt_id: $attempt_id, duration_seconds: $duration_seconds) {
      attempt_id score total_questions duration_seconds status
    }
  }
`;

// hh:mm:ss when there's an hour or more, else mm:ss
const fmt = (s) => {
  s = Math.max(0, s);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
};

class FoundationTestRunner extends Component {
  state = {
    loading: true,
    error: "",
    attemptId: null,
    questions: [],
    current: 0,
    answers: {},        // question_id -> option id
    visited: {},        // question_id -> true (opened at least once)
    secondsLeft: 0,
    submitting: false,
    result: null,
  };

  componentDidMount() {
    this.startedAt = Date.now();
    this.questionShownAt = Date.now();
    this.loadTest();
  }

  componentWillUnmount() {
    clearInterval(this.timer);
  }

  get perQuestionSeconds() {
    return Number(this.props.perQuestionSeconds) || PER_QUESTION_SECONDS;
  }

  loadTest = async () => {
    const { client, chapterId, classId, subjectId, registrationId, mode, subjectIds, chapterIds, topics } = this.props;
    const isError = mode === "error";
    const isCustom = mode === "custom";
    let payload;
    if (isError) {
      payload = {
        mutation: START_ERROR,
        variables: {
          registration_id: registrationId != null ? Number(registrationId) : null,
          class_id: classId != null ? Number(classId) : null,
          subject_ids: (subjectIds || []).map(Number),
          chapter_ids: (chapterIds || []).map(Number),
          limit: 30,
        },
      };
    } else if (isCustom) {
      payload = {
        mutation: START_CUSTOM,
        variables: {
          registration_id: registrationId != null ? Number(registrationId) : null,
          class_id: classId != null ? Number(classId) : null,
          subject_ids: (subjectIds || []).map(Number),
          topics: topics || [],
          limit: 30,
        },
      };
    } else {
      payload = {
        mutation: START,
        variables: {
          chapter_id: Number(chapterId),
          class_id: classId != null ? Number(classId) : null,
          subject_id: subjectId != null ? Number(subjectId) : null,
          registration_id: registrationId != null ? Number(registrationId) : null,
          limit: 30,
        },
      };
    }
    try {
      const { data } = await client.mutate(payload);
      const res = data && (isError ? data.startFoundationErrorTest : isCustom ? data.startFoundationCustomTest : data.startFoundationTest);
      if (!res || !res.attempt_id || !res.questions.length) {
        this.setState({
          loading: false,
          error: isError
            ? "No error questions found for your selection. You have no wrong answers in these chapters yet."
            : isCustom
            ? "No questions found for the selected subjects and topics."
            : "No questions available for this chapter yet.",
        });
        return;
      }
      const firstId = res.questions[0] && res.questions[0].question_id;
      this.setState(
        {
          loading: false,
          attemptId: res.attempt_id,
          questions: res.questions,
          // time scales with the number of questions
          secondsLeft: res.questions.length * this.perQuestionSeconds,
          visited: firstId != null ? { [firstId]: true } : {},
        },
        this.startTimer
      );
      this.questionShownAt = Date.now();
    } catch (e) {
      this.setState({ loading: false, error: "Could not start the test. Please try again." });
    }
  };

  startTimer = () => {
    this.timer = setInterval(() => {
      this.setState(
        (s) => ({ secondsLeft: s.secondsLeft - 1 }),
        () => { if (this.state.secondsLeft <= 0) this.handleSubmit(); }
      );
    }, 1000);
  };

  choose = (questionId, optionId) => {
    this.setState((s) => ({ answers: { ...s.answers, [questionId]: optionId } }));
  };

  clearSelection = () => {
    const q = this.state.questions[this.state.current];
    if (!q) return;
    this.setState((s) => {
      const answers = { ...s.answers };
      delete answers[q.question_id];
      return { answers };
    });
  };

  // persist the current question's answer + the time spent on it
  persistCurrent = async () => {
    const { attemptId, questions, current, answers } = this.state;
    const q = questions[current];
    if (!q) return;
    const spent = Math.max(0, Math.round((Date.now() - this.questionShownAt) / 1000));
    try {
      await this.props.client.mutate({
        mutation: SAVE,
        variables: {
          attempt_id: attemptId,
          question_id: q.question_id,
          selected_option: answers[q.question_id] || null,
          time_taken_seconds: spent,
        },
      });
    } catch (e) { /* keep going; a failed save shouldn't block the test */ }
  };

  // move to an absolute question index
  goTo = async (index) => {
    const { questions, current } = this.state;
    if (index === current || index < 0 || index >= questions.length) return;
    await this.persistCurrent();
    const q = questions[index];
    this.setState(
      (s) => ({
        current: index,
        visited: q ? { ...s.visited, [q.question_id]: true } : s.visited,
      }),
      () => { this.questionShownAt = Date.now(); }
    );
  };

  go = (dir) => this.goTo(this.state.current + dir);

  handleSubmit = async () => {
    if (this.state.submitting) return;
    this.setState({ submitting: true });
    clearInterval(this.timer);
    await this.persistCurrent();
    const duration = Math.round((Date.now() - this.startedAt) / 1000);
    try {
      const { data } = await this.props.client.mutate({
        mutation: SUBMIT,
        variables: { attempt_id: this.state.attemptId, duration_seconds: duration },
      });
      this.setState({ result: (data && data.submitFoundationTest) || null, submitting: false });
    } catch (e) {
      this.setState({ submitting: false, error: "Could not submit the test." });
    }
  };

  confirmFinish = () => {
    const { answers, questions } = this.state;
    const left = questions.length - Object.keys(answers).length;
    const msg = left > 0
      ? `You have ${left} unanswered question${left > 1 ? "s" : ""}. Submit the test anyway?`
      : "Submit the test?";
    if (window.confirm(msg)) this.handleSubmit();
  };

  // colour state of a palette cell
  cellStatus = (q, idx) => {
    if (idx === this.state.current) return "is-current";
    if (this.state.answers[q.question_id]) return "is-answered";
    if (this.state.visited[q.question_id]) return "is-seen";
    return "";
  };

  render() {
    const { chapterName, onClose } = this.props;
    const { loading, error, questions, current, answers, secondsLeft, result, submitting } = this.state;

    if (loading) {
      return (
        <div className="ftest-page fdn-theme">
          <div className="ftest-empty ftest-state ftest-loading">
            <div className="ftest-loader" aria-hidden="true"><span /><span /><span /></div>
            <h4>Getting your test ready ✏️</h4>
            <p>Loading test…</p>
          </div>
        </div>
      );
    }
    if (error) {
      return (
        <div className="ftest-page fdn-theme">
          <div className="ftest-empty ftest-state ftest-state--error">
            <div className="ftest-state-icon"><i className="fas fa-exclamation" /></div>
            <h4>Oops! 🙈</h4>
            <p>{error}</p>
            <button className="ftest-btn fdn-btn fdn-btn--blue fdn-btn--lg" onClick={onClose}>Close</button>
          </div>
        </div>
      );
    }
    if (result) {
      return (
        <div className="ftest-page fdn-theme">
          <div className="ftest-result ftest-state fdn-pop">
            <div className="ftest-result-emoji" aria-hidden="true">🎉</div>
            <div className="ftest-result-score">{result.score}<span>/ {result.total_questions}</span></div>
            <div
              className="ftest-result-bar fdn-progress"
              style={{ "--value": result.total_questions ? Math.round((result.score / result.total_questions) * 100) : 0 }}
            />
            <h3>Test submitted</h3>
            <p>You answered {result.score} of {result.total_questions} correctly in {fmt(result.duration_seconds)}.</p>
            <button className="ftest-btn fdn-btn fdn-btn--blue fdn-btn--lg" onClick={onClose}><i className="fas fa-arrow-left" /> Back to tests</button>
          </div>
        </div>
      );
    }

    const q = questions[current];
    const answeredCount = Object.keys(answers).length;
    const low = secondsLeft <= 300;
    const selected = q ? answers[q.question_id] : null;

    return (
      <div className="ftest-page fdn-theme">
        <header className="ftest-topbar">
          <span className="ftest-brand-icon" aria-hidden="true"><i className="fas fa-pencil-alt" /></span>
          <div className="ftest-brand">{chapterName || "Test"}</div>
          <div className={`ftest-timer ${low ? "is-low" : ""}`}>
            <i className="fas fa-clock" /> <span className="ftest-timer-label">Time Left:</span> <b>{fmt(secondsLeft)}</b>
          </div>
          <button className="ftest-close" onClick={onClose} aria-label="Close">&times;</button>
        </header>

        <div className="ftest-body">
          <main className="ftest-main">
            <div className="ftest-qhead">
              <span className="ftest-qno">Question No: <b>{current + 1}</b><small> / {questions.length}</small></span>
              <span className="ftest-qtype fdn-badge fdn-badge--blue"><i className="far fa-dot-circle" /> Single Choice Type Question</span>
            </div>
            <div
              className="ftest-qprogress fdn-progress"
              style={{ "--value": questions.length ? Math.round(((current + 1) / questions.length) * 100) : 0 }}
              aria-hidden="true"
            />

            <div className="ftest-q">
              <div className="ftest-q-text" dangerouslySetInnerHTML={{ __html: q.question }} />
              <div className="ftest-opts">
                {q.options.map((op) => (
                  <button
                    key={op.id}
                    type="button"
                    className={`ftest-opt ${selected === op.id ? "is-sel" : ""}`}
                    onClick={() => this.choose(q.question_id, op.id)}
                  >
                    <span className="ftest-opt-id">{op.id}</span>
                    <span className="ftest-opt-text" dangerouslySetInnerHTML={{ __html: op.text }} />
                    <span className="ftest-opt-tick" aria-hidden="true"><i className="fas fa-check" /></span>
                  </button>
                ))}
              </div>
            </div>

            <footer className="ftest-foot">
              <button className="ftest-btn ghost fdn-btn" disabled={current === 0} onClick={() => this.go(-1)}>
                <i className="fas fa-arrow-left" /> Previous
              </button>
              <div className="ftest-foot-mid">
                <button className="ftest-btn ghost fdn-btn" disabled={!selected} onClick={this.clearSelection}>
                  <i className="fas fa-eraser" /> Clear Selection
                </button>
                {current < questions.length - 1 && (
                  <button className="ftest-btn fdn-btn fdn-btn--blue" onClick={() => this.go(1)}>Save &amp; Next <i className="fas fa-arrow-right" /></button>
                )}
              </div>
              <button className="ftest-btn solid fdn-btn fdn-btn--primary" disabled={submitting} onClick={this.confirmFinish}>
                {submitting ? "Submitting…" : "Finish"}
              </button>
            </footer>
          </main>

          <aside className="ftest-side">
            <div className="ftest-side-title"><i className="fas fa-th" /> Question Overview</div>
            <div className="ftest-palette">
              {questions.map((qq, idx) => (
                <button
                  key={qq.question_id}
                  type="button"
                  className={`ftest-cell ${this.cellStatus(qq, idx)}`}
                  onClick={() => this.goTo(idx)}
                >
                  {idx + 1}
                </button>
              ))}
            </div>

            <ul className="ftest-legend">
              <li><span className="dot is-answered" /> Answered <b>{answeredCount}</b></li>
              <li><span className="dot is-seen" /> Not answered <b>{Math.max(0, questions.length - answeredCount)}</b></li>
              <li><span className="dot" /> Not visited</li>
            </ul>

            <button className="ftest-finish fdn-btn fdn-btn--primary fdn-btn--lg fdn-btn--block" disabled={submitting} onClick={this.confirmFinish}>
              {submitting ? "Submitting…" : <>Finish Test <i className="fas fa-flag-checkered" /></>}
            </button>
          </aside>
        </div>
      </div>
    );
  }
}

export default withApollo(FoundationTestRunner);
